/**
 * 任务状态全局存储（Valtio proxy）。
 * 管理画布中所有任务的运行时状态列表。
 */

import { proxy } from 'valtio';
import { buildOssThumbnailUrl, buildVideoPosterPreviewUrl, ossThumbnailTiers } from '@/shared/lib/oss-url-utils';
import type { TaskPhase, TaskState } from './task-runtime-state';
import { resolveTaskCategory } from '@/shared/lib/task/task-category';
import { yCustomNodeDatas } from '@/shared/config/yjs-config';

/** 服务端任务列表项（HTTP API 返回格式） */
export interface ServerTaskItem {
  id: string;
  task_id: string;
  task_type: string;
  key: string;
  plugin_id: string;
  name?: string;
  node_id?: string;
  node_type?: string;
  progress: number;
  result?: string;
  detail?: string | Record<string, any>;
  extra?: string | Record<string, any>;
  create_time: number;
  start_time?: number;
  run_id?: string;
  module?: string;
  accelerate?: number;
  upload_image?: { url?: string; thumb?: string };
  process?: {
    code?: number;
    data?: {
      wait_count?: number;
      outputs?: Array<Array<{
        url?: string;
        thumb?: string;
        material_id?: number | string;
        width?: number;
        height?: number;
        size?: number;
        duration?: number;
        check_status?: number;
      }>>;
    };
  };
  check_status?: number;
}

interface TaskStoreState {
  taskStates: TaskState[];
  pendingRuns: { nodeId: string; runId: string }[];
  totalCounts: Record<string, number | undefined>;
}

export const taskStore = proxy<TaskStoreState>({
  taskStates: [],
  pendingRuns: [],
  totalCounts: {},
});

export function updateTaskTotalCount(category: string, total: number) {
  if (typeof total === 'number' && !isNaN(total)) {
    taskStore.totalCounts[category] = total;
  }
}

function isSameShallow(left?: Record<string, any>, right?: Record<string, any>) {
  if (left === right) return true;
  if (!left || !right) return !left && !right;
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);
  if (leftEntries.length !== rightEntries.length) return false;
  return leftEntries.every(([key, value]) => Object.is(right[key], value));
}

/**
 * 从任务 outputs 中读取第一个媒体结果。
 *
 * @param outputs 后端任务返回的 outputs，当前结构为二维数组 outputs[0][0]
 * @returns 第一条结果对象；结构异常时返回 undefined
 */
function readFirstTaskOutput(outputs: unknown) {
  if (!Array.isArray(outputs)) return undefined;
  const firstGroup = outputs[0];
  if (!Array.isArray(firstGroup)) return undefined;
  const firstOutput = firstGroup[0];
  return firstOutput && typeof firstOutput === 'object'
    ? firstOutput as Record<string, any>
    : undefined;
}

function readAllTaskOutputs(outputs: unknown, nodeType?: string) {
  if (!Array.isArray(outputs)) return undefined;
  const results = [];
  for (const group of outputs) {
    if (!Array.isArray(group)) continue;
    const firstOutput = group[0];
    if (firstOutput && typeof firstOutput === 'object') {
      const url = fixDoubleDomainUrl(firstOutput.url);
      const thumbBase = fixDoubleDomainUrl(firstOutput.thumb) || url;
      let thumb: string | undefined;
      if (thumbBase) {
        if (nodeType === 'video' || thumbBase.includes('.mp4')) {
          thumb = buildVideoPosterPreviewUrl(thumbBase, ossThumbnailTiers[5]);
        } else {
          thumb = buildOssThumbnailUrl(thumbBase, ossThumbnailTiers[5]);
        }
      }
      results.push({
        url,
        thumb,
        material_id: firstOutput.material_id,
        width: firstOutput.width != null ? Number(firstOutput.width) : undefined,
        height: firstOutput.height != null ? Number(firstOutput.height) : undefined,
        size: firstOutput.size != null ? Number(firstOutput.size) : undefined,
        duration: firstOutput.duration != null ? Number(firstOutput.duration) : undefined,
        check_status: firstOutput.check_status,
        text: firstOutput.type === 'text' ? firstOutput.text : undefined,
      });
    }
  }
  return results.length > 0 ? results : undefined;
}

function fixDoubleDomainUrl(url?: string) {
  if (!url) return url;
  const match = url.match(/(https?:\/\/[^/]+)\/(https?:\/\/.+)/);
  if (match) {
    return match[2];
  }
  return url;
}

function readCurrentNodeTaskRuntime(task: Pick<TaskState, 'nodeId' | 'taskId' | 'runId'>) {
  if (!task.nodeId) return undefined;

  const yNodeData = yCustomNodeDatas.get(task.nodeId) as import('yjs').Map<unknown> | undefined;
  if (!yNodeData) return undefined;

  const nodeTaskId = yNodeData.get('taskId') as string | undefined;
  const nodeRunId = yNodeData.get('runId') as string | undefined;
  const isSameTask = Boolean(
    (nodeTaskId && nodeTaskId === task.taskId) ||
    (nodeRunId && task.runId && nodeRunId === task.runId),
  );
  if (!isSameTask) return undefined;

  const status = yNodeData.get('status') as string | undefined;
  let phase: TaskPhase | undefined;
  if (status === 'completed') phase = 'succeeded';
  else if (status === 'failed' || status === 'canceled') phase = 'failed';
  else if (status === 'running') phase = 'processing';
  else if (status === 'pending') phase = 'queued';
  if (!phase) return undefined;

  const progressValue = yNodeData.get('progress');
  const progress = typeof progressValue === 'number'
    ? progressValue
    : phase === 'succeeded'
      ? 100
      : undefined;

  return { phase, progress };
}

export function upsertTaskState(taskState: TaskState) {
  const existingIndex = taskStore.taskStates.findIndex((t) => t.taskId === taskState.taskId);
  if (existingIndex >= 0) {
    const current = taskStore.taskStates[existingIndex];

    if (
      current.service === taskState.service &&
      current.phase === taskState.phase &&
      current.progress === taskState.progress &&
      current.message === taskState.message &&
      current.prompt === taskState.prompt &&
      current.resultImageUrl === taskState.resultImageUrl &&
      current.resultThumbnailUrl === taskState.resultThumbnailUrl &&
      current.resultText === taskState.resultText &&
      current.resultMaterialId === taskState.resultMaterialId &&
      current.resultWidth === taskState.resultWidth &&
      current.resultHeight === taskState.resultHeight &&
      current.resultSize === taskState.resultSize &&
      current.resultDuration === taskState.resultDuration &&
      current.check_status === taskState.check_status &&
      JSON.stringify(current.outputs) === JSON.stringify(taskState.outputs) &&
      isSameShallow(current.queue, taskState.queue) &&
      current.transport?.kind === taskState.transport?.kind
    ) {
      return;
    }

    taskStore.taskStates[existingIndex] = {
      ...current,
      ...taskState,
      updatedAt: taskState.updatedAt || Date.now(),
    };
    taskStore.taskStates.sort((a, b) => b.updatedAt - a.updatedAt);
    return;
  }

  if (typeof taskStore.totalCounts.all === 'number') {
    taskStore.totalCounts.all++;
  }
  const cat = resolveTaskCategory(taskState.service, taskState.nodeType);
  if (typeof taskStore.totalCounts[cat] === 'number') {
    taskStore.totalCounts[cat]!++;
  }

  taskStore.taskStates.unshift({
    ...taskState,
    updatedAt: taskState.updatedAt || Date.now(),
  });
}

export function syncServerTasks(items: ServerTaskItem[]) {
  items.forEach((item) => {
    let nodeId: string | undefined = item.node_id;
    let extraObj: any;
    try {
      extraObj = typeof item.extra === 'string' ? JSON.parse(item.extra) : item.extra;
      if (!nodeId) nodeId = extraObj?.nodeId || extraObj?.node_id;
    } catch {
      // ignore
    }

    const existingIndex = taskStore.taskStates.findIndex((t) => t.taskId === item.task_id);

    let prompt: string | undefined = extraObj?.prompt;
    let detailObj: Record<string, any> | undefined;
    try {
      if (typeof item.detail === 'string' && item.detail.length > 0) {
        try {
          detailObj = JSON.parse(item.detail);
        } catch {
          const binary = atob(item.detail);
          const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
          const decoded = new TextDecoder().decode(bytes);
          detailObj = JSON.parse(decoded);
        }
      } else if (item.detail && typeof item.detail === 'object') {
        detailObj = item.detail;
      }
      if (!prompt && detailObj) {
        prompt = detailObj?.work_datas?.prompt || detailObj?.prompt;
      }
    } catch {
      // ignore
    }

    const waitCount = item.process?.data?.wait_count ?? detailObj?.process_data?.wait_count ?? 0;
    let phase: TaskPhase = 'processing';
    if (item.progress === 100) {
      phase = 'succeeded';
    } else if (item.progress === -1) {
      phase = 'failed';
    } else if (item.progress === 0 && waitCount > 0) {
      phase = 'queued';
    }

    let resultImageUrl: string | undefined;
    let resultThumbnailUrl: string | undefined;
    let resultText: string | undefined;
    let resultMaterialId: string | number | undefined;
    let resultWidth: number | undefined;
    let resultHeight: number | undefined;
    let resultSize: number | undefined;
    let resultDuration: number | undefined;
    const outputsToRead = item.process?.data?.outputs || detailObj?.process_data?.outputs;
    const firstOutput = readFirstTaskOutput(outputsToRead);

    if (firstOutput) {
      resultImageUrl = fixDoubleDomainUrl(firstOutput.url);
      const thumbBase = fixDoubleDomainUrl(firstOutput.thumb) || resultImageUrl;
      if (thumbBase) {
        if (item.node_type === 'video' || thumbBase.includes('.mp4')) {
          resultThumbnailUrl = buildVideoPosterPreviewUrl(thumbBase, ossThumbnailTiers[5]);
        } else {
          resultThumbnailUrl = buildOssThumbnailUrl(thumbBase, ossThumbnailTiers[5]);
        }
      }
      if (firstOutput.type === 'text' && firstOutput.text) {
        resultText = firstOutput.text;
      }
      resultMaterialId = firstOutput.material_id;
      resultWidth = firstOutput.width != null ? Number(firstOutput.width) : undefined;
      resultHeight = firstOutput.height != null ? Number(firstOutput.height) : undefined;
      resultSize = firstOutput.size != null ? Number(firstOutput.size) : undefined;
      resultDuration = firstOutput.duration != null ? Number(firstOutput.duration) : undefined;
    } else if (detailObj?.result && Array.isArray(detailObj.result) && detailObj.result.length > 0) {
      // Fallback for when process_data is completely missing but detail.result has URLs
      const url = detailObj.result[0];
      if (typeof url === 'string') {
        resultImageUrl = fixDoubleDomainUrl(url);
        if (item.node_type === 'video' || resultImageUrl?.includes('.mp4')) {
          resultThumbnailUrl = buildVideoPosterPreviewUrl(resultImageUrl!, ossThumbnailTiers[5]);
        } else {
          resultThumbnailUrl = buildOssThumbnailUrl(resultImageUrl!, ossThumbnailTiers[5]);
        }
      }
    }

    let service = item.key;
    if (item.node_type === 'image') service = 'qianlu-image';
    else if (item.node_type === 'video') service = 'qianlu-video';
    else if (item.node_type === 'text') service = 'ai-canvas-text';
    else if (item.node_type === 'audio') service = 'ai-canvas-audio-generation';

    const serverTaskState: TaskState = {
      id: item.id,
      taskId: item.task_id,
      nodeId,
      service,
      phase,
      progress: item.progress === -1 ? 0 : item.progress,
      message: item.result,
      createdAt: item.create_time * 1000,
      updatedAt: (item.start_time || item.create_time) * 1000,
      resultImageUrl,
      resultThumbnailUrl,
      resultText,
      resultMaterialId,
      resultWidth,
      resultHeight,
      resultSize,
      resultDuration,
      check_status: item.check_status ?? firstOutput?.check_status,
      outputs: readAllTaskOutputs(outputsToRead, item.node_type),
      prompt,
      detail: detailObj,
      uploadImage: item.upload_image ? { url: item.upload_image.url, thumb: item.upload_image.thumb } : undefined,
      taskName: item.name,
      nodeType: item.node_type,
      runId: item.run_id,
      submitParams: {
        task_type: item.task_type,
        key: item.key,
        plugin_id: item.plugin_id,
        extra: extraObj,
        module: item.module,
        accelerate: item.accelerate,
        rawDetail: typeof item.detail === 'string' ? item.detail : undefined,
      },
    };
    const nodeRuntime = readCurrentNodeTaskRuntime(serverTaskState);
    const taskState: TaskState = nodeRuntime
      ? {
        ...serverTaskState,
        phase: nodeRuntime.phase,
        progress: nodeRuntime.progress ?? serverTaskState.progress,
      }
      : serverTaskState;

    if (existingIndex >= 0) {
      taskStore.taskStates[existingIndex] = taskState;
    } else {
      taskStore.taskStates.push(taskState);
    }
  });

  taskStore.taskStates.sort((a, b) => b.createdAt - a.createdAt);
}

export function clearTaskState(args: { taskId?: string; nodeId?: string }) {
  const removedTasks = taskStore.taskStates.filter((t) => {
    if (args.taskId) return t.taskId === args.taskId;
    if (args.nodeId) return t.nodeId === args.nodeId;
    return false;
  });

  if (removedTasks.length > 0) {
    if (args.taskId) {
      taskStore.taskStates = taskStore.taskStates.filter((t) => t.taskId !== args.taskId);
    } else if (args.nodeId) {
      taskStore.taskStates = taskStore.taskStates.filter((t) => t.nodeId !== args.nodeId);
    }

    removedTasks.forEach((t) => {
      if (typeof taskStore.totalCounts.all === 'number' && taskStore.totalCounts.all > 0) {
        taskStore.totalCounts.all--;
      }
      const cat = resolveTaskCategory(t.service, t.nodeType);
      if (typeof taskStore.totalCounts[cat] === 'number' && taskStore.totalCounts[cat]! > 0) {
        taskStore.totalCounts[cat]!--;
      }
    });
  }
}

export function clearAllTasks() {
  taskStore.taskStates = [];
}

export function clearActiveTaskStates(args?: { nodeId?: string }) {
  taskStore.taskStates = taskStore.taskStates.filter((task) => {
    if (args?.nodeId && task.nodeId !== args.nodeId) return true;
    return task.phase === 'succeeded' || task.phase === 'failed';
  });
}

export function addPendingRun(nodeId: string, runId: string) {
  taskStore.pendingRuns.push({ nodeId, runId });
}

export function removePendingRunByRunId(runId: string) {
  const index = taskStore.pendingRuns.findIndex((p) => p.runId === runId);
  if (index !== -1) {
    taskStore.pendingRuns.splice(index, 1);
    return true;
  }
  return false;
}
